import React, { useCallback, useEffect, useRef, useState } from "react";
import { adsPipelineStatus } from "../../services/api";

/**
 * Always-visible state of the ADS background pipeline.
 *
 * Ingestion, screenshot capture and image reads happen on a worker, away from
 * the request that asked for them. Without this strip, three very different
 * situations all looked the same from the UI, and all of them looked like
 * nothing happening:
 *
 *   - a worker is grinding through a backlog
 *   - no worker is running, so the queue fills and never drains
 *   - the provider is refusing captures and the breaker has paused everything
 *
 * So the strip is deliberately present even when idle. A status surface that
 * only appears during trouble cannot be trusted as an answer to "is it still
 * going?", because its absence is ambiguous.
 *
 * Polling follows the state: quick while something is moving or paused (so a
 * countdown stays honest), slow when idle, and stopped entirely when the tab is
 * hidden, since nobody is reading it.
 */

const POLL_ACTIVE_MS = 4000;
const POLL_IDLE_MS = 20000;

const TONES = {
  paused:   { bg: "#fffbeb", border: "#fde68a", fg: "#92400e", dot: "#d97706", label: "Paused" },
  stalled:  { bg: "#fef2f2", border: "#fecaca", fg: "#b91c1c", dot: "#dc2626", label: "Stalled" },
  degraded: { bg: "#fef2f2", border: "#fecaca", fg: "#b91c1c", dot: "#dc2626", label: "Degraded" },
  working:  { bg: "#eff6ff", border: "#bfdbfe", fg: "#1d4ed8", dot: "#2563eb", label: "Working" },
  idle:     { bg: "#f9fafb", border: "#e5e7eb", fg: "#4b5563", dot: "#9ca3af", label: "Idle" },
};

function fmtAge(seconds) {
  if (seconds == null) return "never";
  const s = Number(seconds);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
}

export default function PipelineStatusStrip() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [open, setOpen] = useState(false);
  // Counted down locally between polls so a pause reads as a live countdown
  // rather than a number that jumps every few seconds.
  const [secsLeft, setSecsLeft] = useState(0);
  const timer = useRef(null);
  // Kept in a ref so the polling loop can read the latest state to choose its
  // next delay, without the loop being torn down and rebuilt on every response.
  const dataRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const d = await adsPipelineStatus();
      setData(d);
      setErr(null);
      setSecsLeft(d?.capture?.seconds_remaining || 0);
    } catch (e) {
      // The strip reporting its own failure is still useful information: it
      // means the backend is not answering, which is itself a pipeline state.
      setErr(e?.message || "Could not read pipeline status.");
    }
  }, []);

  // Poll at a cadence that matches the state, and not at all when unseen.
  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      if (!document.hidden) await load();
      if (cancelled) return;
      const state = dataRef.current?.summary?.state;
      const active = state === "working" || state === "paused" || state === "stalled" || state === "degraded";
      timer.current = setTimeout(tick, active ? POLL_ACTIVE_MS : POLL_IDLE_MS);
    };

    tick();
    // A hidden tab stops polling; coming back re-reads immediately so the strip
    // is never showing a stale minute-old picture the moment it is looked at.
    const onVisible = () => { if (!document.hidden) load(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      if (timer.current) clearTimeout(timer.current);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [load]);

  useEffect(() => { dataRef.current = data; }, [data]);

  // Local countdown for the pause, so the minutes tick down between polls.
  useEffect(() => {
    if (!secsLeft) return undefined;
    const iv = setInterval(() => setSecsLeft(s => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(iv);
  }, [secsLeft > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err && !data) {
    return (
      <div style={{
        padding: "8px 12px", marginBottom: 10, borderRadius: 10,
        background: "#fef2f2", border: "1px solid #fecaca",
        color: "#b91c1c", fontSize: 12.5, fontWeight: 700,
      }}>
        Background pipeline status unavailable: {err}
      </div>
    );
  }
  if (!data) return null;

  const summary = data.summary || {};
  const tone = TONES[summary.state] || TONES.idle;
  const adsQ = (data.queues || []).find(q => q.name === "ads") || {};
  const inFlight = data.in_flight || {};
  // The monitor cron is nearly always "in progress" by ARQ's reckoning, so the
  // counters follow work_total. Counting it would show a permanent "1 running".
  const running = inFlight.work_total ?? inFlight.total ?? 0;
  const capture = data.capture || {};

  // Prefer the locally ticking countdown over the polled number.
  const mins = secsLeft > 0 ? Math.max(1, Math.round(secsLeft / 60)) : 0;
  const headline = capture.open && mins
    ? `Screenshot captures are paused for about ${mins} ${mins === 1 ? "minute" : "minutes"}.`
    : summary.headline;

  return (
    <div style={{
      marginBottom: 10, borderRadius: 10, overflow: "hidden",
      background: tone.bg, border: `1px solid ${tone.border}`,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", flexWrap: "wrap" }}>
        <span style={{
          width: 9, height: 9, borderRadius: 99, background: tone.dot, flexShrink: 0,
          // A pulse is the cheapest honest signal that this is live data and
          // not a number frozen when the page loaded.
          animation: summary.state === "working" ? "adsPulse 1.2s ease-in-out infinite" : "none",
        }} />
        <span style={{ fontSize: 11, fontWeight: 900, color: tone.fg, letterSpacing: 0.3, textTransform: "uppercase" }}>
          {tone.label}
        </span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: tone.fg }}>
          {headline}
        </span>

        {/* Live counters, so a long backlog is legible at a glance. */}
        {(running > 0 || adsQ.queued > 0) && (
          <span style={{ fontSize: 12, fontWeight: 700, color: tone.fg, opacity: 0.85 }}>
            · {running} running · {adsQ.queued} queued
            {adsQ.scheduled_later > 0 ? ` · ${adsQ.scheduled_later} scheduled` : ""}
          </span>
        )}

        <div style={{ flex: 1 }} />
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            border: `1px solid ${tone.border}`, background: "#fff", color: tone.fg,
            borderRadius: 7, padding: "3px 9px", cursor: "pointer", boxShadow: "none", transform: "none",
            fontSize: 11.5, fontWeight: 800, whiteSpace: "nowrap",
          }}
        >
          {open ? "Hide" : "Details"}
        </button>
      </div>

      {summary.detail && !open && (
        <div style={{ padding: "0 12px 8px", fontSize: 12, color: tone.fg, opacity: 0.85, lineHeight: 1.5 }}>
          {summary.detail}
        </div>
      )}

      {open && (
        <div style={{
          borderTop: `1px solid ${tone.border}`, background: "#fff",
          padding: "10px 12px", fontSize: 12, color: "#374151", lineHeight: 1.7,
        }}>
          {summary.detail && (
            <div style={{ marginBottom: 8, fontWeight: 700, color: tone.fg }}>{summary.detail}</div>
          )}

          <div style={{ display: "grid", gap: 3 }}>
            {(data.queues || []).map(q => (
              <div key={q.name}>
                <b>{q.label}</b>{" — "}
                {q.error
                  ? <span style={{ color: "#b91c1c" }}>{q.error}</span>
                  : <>
                      worker {q.worker_alive
                        ? <span style={{ color: "#047857", fontWeight: 800 }}>running</span>
                        : <span style={{ color: "#b91c1c", fontWeight: 800 }}>not running</span>}
                      {", "}{q.queued ?? 0} queued
                      {q.scheduled_later > 0 ? `, ${q.scheduled_later} scheduled for later` : ""}
                      {q.worker_alive && q.heartbeat_reliable === false && (
                        <span style={{ color: "#92400e" }}>
                          {" "}(heartbeat is from an older worker build, so liveness is approximate)
                        </span>
                      )}
                    </>}
              </div>
            ))}

            <div>
              <b>Running now</b>{" — "}
              {running > 0
                ? Object.entries(inFlight.by_function || {})
                    .filter(([fn]) => fn !== "cron")
                    .map(([fn, n]) => `${fn} ×${n}`).join(", ")
                : "nothing"}
              {inFlight.cron_total > 0 && (
                <span style={{ color: "#9ca3af" }}>
                  {" "}(plus the monitor schedule tick, which runs every minute)
                </span>
              )}
            </div>

            <div>
              <b>Last image read attempt</b>{" — "}
              {fmtAge(data.extraction?.seconds_since_last_attempt)}
              {data.extraction?.attempts_last_hour > 0
                ? ` (${data.extraction.attempts_last_hour} in the last hour)`
                : ""}
            </div>

            <div>
              <b>Screenshot captures</b>{" — "}
              {capture.open
                ? <span style={{ color: "#92400e", fontWeight: 800 }}>
                    paused, about {mins} min left{capture.trips > 1 ? ` (refusal #${capture.trips})` : ""}
                  </span>
                : <span style={{ color: "#047857", fontWeight: 800 }}>allowed</span>}
              {capture.last_reason ? <span style={{ color: "#6b7280" }}> · last refusal: {capture.last_reason}</span> : null}
            </div>
          </div>

          <div style={{ marginTop: 8, fontSize: 11, color: "#9ca3af" }}>
            Refreshes automatically. Read at {new Date(data.as_of).toLocaleTimeString()}.
            {err ? ` Last refresh failed: ${err}` : ""}
          </div>
        </div>
      )}

      <style>{"@keyframes adsPulse{0%,100%{opacity:1}50%{opacity:0.25}}"}</style>
    </div>
  );
}
